import { FC } from 'react';
import { SxProps, TextField, Theme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { MAX_ASSESSMENT_TITLE_LENGTH } from '@libs/constants';
import { useTrans } from '@libs/i18n';

import type { useDuplicateTitleField } from '../../hooks/useDuplicateTitleField';

interface DuplicateTitleFieldProps {
  /**
   * State from `useDuplicateTitleField`. The dialog owns it rather than this
   * component so it can gate its own confirm button on `isValid`.
   */
  field: ReturnType<typeof useDuplicateTitleField>;
  /** Enter submits, matching the dialog's confirm button. */
  onSubmit: () => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * The editable title in both duplicate dialogs. Only the surrounding chrome
 * differs between them (icons, button variants, palette), so the input — and in
 * particular the Enter-to-submit handler and the error/counter helper text —
 * lives here rather than being kept in sync by hand in two places.
 *
 * Deliberately no `maxLength`: a hard cap would silently swallow the tail of a
 * pasted title. An over-long one shows the error instead.
 */
export const DuplicateTitleField: FC<DuplicateTitleFieldProps> = observer(({
  field,
  onSubmit,
  size,
  disabled = false,
  sx,
}) => {
  const { t } = useTrans();

  return (
    <TextField
      autoFocus
      fullWidth
      size={size}
      label={t('app.assessments.duplicate.titleLabel')}
      value={field.title}
      onChange={(event) => field.setTitle(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          onSubmit();
        }
      }}
      error={!!field.error}
      helperText={field.error || `${field.title.length}/${MAX_ASSESSMENT_TITLE_LENGTH}`}
      disabled={disabled}
      sx={sx}
    />
  );
});
